import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { HiOutlineDownload } from "react-icons/hi";
import FinoDmtService from "../../../services/FinoDmtService";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import DataTableComponent from "react-data-table-component";
import SideBar from "../../../components/Sidebar";

const MoneyTransferHistory = () => {
  const today = new Date().toISOString().split("T")[0];

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [searchText, setSearchText] = useState("");
  const [isInitState, setIsInitState] = useState(true);
  const [size, setSize] = useState(10);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, [
    //  fromDate, toDate, searchText,
    page,
    size,
  ]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await FinoDmtService.fundTransferHistory(
        fromDate,
        toDate,
        searchText,
        isInitState,
        "",
        size,
        page,
      );

      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        setData(responseData.data.result);
        setTotalRows(responseData.data.total_count || 0);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderCell = (value) => (
    <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
      {value || "-"}
    </div>
  );

  const getStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case "SUCCESS":
        return <span className="badge bg-success">{status}</span>;
      case "FAILED":
      case "REFUND":
        return <span className="badge bg-danger">{status}</span>;
      case "PENDING":
        return <span className="badge bg-warning">{status}</span>;
      default:
        return <span className="badge bg-secondary">{status || "-"}</span>;
    }
  };

  const handleDownloadReceipt = async (orderId) => {
    try {
      setLoading(true);
      const result = await FinoDmtService.downloadReceipt(orderId);

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Transaction failed", "error");
        setLoading(false);
        return;
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        const link = responseData.data?.link;
        if (link) {
          window.open(link, "_blank");
        }
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", error?.message || "Network error", "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => (page - 1) * size + index + 1,
      width: "70px",
    },
    {
      name: "Date",
      selector: (row) => row.created_on,
      cell: (row) => renderCell(row.created_on),
      sortable: true,
    },
    {
      name: "Order ID",
      selector: (row) => row.order_id,
      cell: (row) => renderCell(row.order_id),
    },
    {
      name: "Sender Number",
      selector: (row) => row.optional1,
      cell: (row) => renderCell(row.optional1),
    },
    {
      name: "Bank Name",
      selector: (row) => row.optional3,
      cell: (row) => renderCell(row.optional3),
    },
    {
      name: "Beneficiary",
      cell: (row) =>
        renderCell(
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            <div>
              <strong>Name:</strong> {row.optional4}
            </div>
            <div>
              <strong>Account:</strong> {row.optional5}
            </div>
            <div>
              <strong>IFSC:</strong> {row.optional6}
            </div>
          </div>,
        ),
    },
    {
      name: "RRN",
      selector: (row) => row.operator_id,
      cell: (row) => renderCell(row.operator_id),
    },
    {
      name: "Transfer Amount",
      selector: (row) => `₹${row.order_amount || 0}`,
      cell: (row) => renderCell(`₹${row.order_amount || 0}`),
    },
    // ⭐ NEW COMMISSION COLUMN ⭐
    {
      name: "Commission",
      selector: (row) => `₹${row.commercial_amount || 0}`,
      cell: (row) => renderCell(`₹${row.commercial_amount || 0}`),
    },
    {
      name: "Amount Debited",
      selector: (row) => `₹${row.amount || 0}`,
      cell: (row) => renderCell(`₹${row.amount || 0}`),
    },
    {
      name: "Status",
      cell: (row) => getStatusBadge(row.status),
      width: "120px",
    },
    {
      name: "Action",
      cell: (row) =>
        renderCell(
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            <button
              className="btn btn-primary btn-xs"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: "0.15rem 0.4rem",
                fontSize: "0.7rem",
                whiteSpace: "nowrap",
              }}
              onClick={() => handleDownloadReceipt(row.order_id)}
            >
              <HiOutlineDownload size={12} />
              Receipt
            </button>
          </div>,
        ),
    },
  ];

  const customStyles = {
    table: {
      style: {
        minWidth: "1400px",
      },
    },
    headCells: {
      style: {
        padding: "6px",
        fontSize: "13px",
        fontWeight: "600",
        whiteSpace: "nowrap",
        backgroundColor: "#000", // BLACK BACKGROUND
        color: "#fff", // WHITE TEXT
        borderBottom: "1px solid #444",
      },
    },
    cells: {
      style: {
        padding: "6px",
        fontSize: "13px",
        whiteSpace: "nowrap",
      },
    },
  };

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setSize(newPerPage);
    setPage(page);
  };

  return (
    <div className="container-fluid mt-3">
      {loading && <LoadingScreen />}

      <div className="row">
        <SideBar />
        <div className="col-md-9 mb-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title pb-2">Fino Money Transfer History</h5>

              <div className="mb-3 text-end">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </button>
              </div>

              {showFilters && (
                <div className="ps-3 pe-3">
                  <div className="row mb-4 border p-3 rounded bg-light">
                    <div className="col-md-3">
                      <label>From Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={fromDate}
                        onChange={(e) => {
                          setFromDate(e.target.value);
                          setIsInitState(false);
                        }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label>To Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={toDate}
                        onChange={(e) => {
                          setToDate(e.target.value);
                          setIsInitState(false);
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label>Search</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Mobile, Email, Order ID, Ack No"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      <button
                        className="btn btn-primary w-100"
                        onClick={fetchData}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Scrollable Table */}
              <div style={{ overflowX: "auto", maxHeight: "70vh" }}>
                <DataTableComponent
                  columns={columns}
                  data={data}
                  progressPending={loading}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  paginationPerPage={size}
                  paginationDefaultPage={page}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                  highlightOnHover
                  striped
                  persistTableHead
                  responsive={false}
                  noDataComponent="No records found"
                  customStyles={customStyles}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoneyTransferHistory;
